import React, { useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { DropdownMenu } from "./DropdownMenu";
import { compactDensity } from "../../contexts/presets";

const compactDensityOverride: React.CSSProperties = {
  "--flysoft-density-padding-x-sm": compactDensity.paddingX.sm,
  "--flysoft-density-padding-x-md": compactDensity.paddingX.md,
  "--flysoft-density-padding-x-lg": compactDensity.paddingX.lg,
  "--flysoft-density-padding-y-sm": compactDensity.paddingY.sm,
  "--flysoft-density-padding-y-md": compactDensity.paddingY.md,
  "--flysoft-density-padding-y-lg": compactDensity.paddingY.lg,
  "--flysoft-density-container-padding-x": compactDensity.containerPaddingX,
  "--flysoft-density-container-padding-y": compactDensity.containerPaddingY,
  "--flysoft-density-gap-sm": compactDensity.gap.sm,
  "--flysoft-density-gap-md": compactDensity.gap.md,
  "--flysoft-density-gap-lg": compactDensity.gap.lg,
  "--flysoft-density-font-xs": compactDensity.fontXs,
  "--flysoft-density-font-sm": compactDensity.fontSm,
  "--flysoft-density-font-base": compactDensity.fontBase,
  "--flysoft-density-font-lg": compactDensity.fontLg,
  "--flysoft-density-font-xl": compactDensity.fontXl,
  "--flysoft-density-datatable-row": compactDensity.dataTableRow,
  "--flysoft-density-datatable-header": compactDensity.dataTableHeader,
} as React.CSSProperties;

interface ActionItem {
  id: string | number;
  content: React.ReactNode;
}

export interface DataTableColumn<T> {
  align?: "left" | "right" | "center";
  width?: string;
  header?: string | React.ReactNode;
  footer?: string | React.ReactNode;
  value?: string | number | ((row: T) => string | React.ReactNode);
  tooltip?: (row: T) => string | React.ReactNode;
  type?: "text" | "numeric" | "currency" | "date";
  /**
   * Acciones para cada fila. Retorna un array de ReactNode que se mostrarán en un DropdownMenu.
   */
  actions?: (row: T) => Array<React.ReactNode>;
  /**
   * Acciones para el header de la columna. Retorna un array de ReactNode que se mostrarán en un DropdownMenu.
   */
  headerActions?: () => Array<React.ReactNode>;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  fixedColumn?: DataTableColumn<T>;
  rows: T[];
  className?: string;
  maxRows?: number; // Máximo número de filas visibles antes de activar scroll
  locale?: string; // Locale para formateo de números (por defecto 'es-AR')
  isLoading?: boolean; // Estado de carga (por defecto false)
  loadingRows?: number; // Número de filas skeleton a mostrar cuando está cargando (por defecto 5)
  /**
   * Función opcional para aplicar clases CSS a una fila específica basada en sus datos.
   */
  rowClassName?: (row: T) => string;
  headerClassName?: string;
  footerClassName?: string;
  headerCellClassName?: string;
  footerCellClassName?: string;
  cellClassName?: string | ((row: T, column: DataTableColumn<T>) => string);
  compact?: boolean;
}

export const DataTable = <T,>({
  columns,
  fixedColumn,
  rows,
  className = "",
  maxRows,
  locale = "es-AR",
  isLoading = false,
  loadingRows = 5,
  rowClassName,
  headerClassName = "",
  footerClassName = "",
  headerCellClassName = "",
  footerCellClassName = "",
  cellClassName = "",
  compact = false,
}: DataTableProps<T>) => {
  const horizontalScrollRef = useRef<HTMLDivElement>(null);
  const customScrollbarTrackRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isThumbDragging, setIsThumbDragging] = useState(false);
  const dragStartX = useRef(0);
  const dragStartScrollLeft = useRef(0);
  const thumbDragOffsetX = useRef(0);
  const [showCustomScrollbar, setShowCustomScrollbar] = useState(false);
  const [scrollbarThumbWidth, setScrollbarThumbWidth] = useState(0);
  const [scrollbarThumbLeft, setScrollbarThumbLeft] = useState(0);

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (
      event.button !== 0 ||
      !event.shiftKey ||
      !horizontalScrollRef.current
    ) {
      return;
    }

    setIsDragging(true);
    dragStartX.current = event.clientX;
    dragStartScrollLeft.current = horizontalScrollRef.current.scrollLeft;
    event.preventDefault();
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !horizontalScrollRef.current) return;

    const deltaX = event.clientX - dragStartX.current;
    horizontalScrollRef.current.scrollLeft = dragStartScrollLeft.current - deltaX;
    event.preventDefault();
  };

  const handleMouseUpOrLeave = () => {
    if (isDragging) {
      setIsDragging(false);
    }
  };

  const updateCustomScrollbar = () => {
    const container = horizontalScrollRef.current;
    if (!container) return;

    const { scrollWidth, clientWidth, scrollLeft } = container;
    const trackWidth = customScrollbarTrackRef.current?.clientWidth ?? clientWidth;
    const hasOverflow = scrollWidth > clientWidth;
    setShowCustomScrollbar(hasOverflow);

    if (!hasOverflow) {
      setScrollbarThumbLeft(0);
      setScrollbarThumbWidth(0);
      return;
    }

    const thumbWidth = Math.min(
      Math.max((clientWidth / scrollWidth) * trackWidth, 40),
      trackWidth,
    );
    const maxThumbTravel = Math.max(trackWidth - thumbWidth, 0);
    const maxScrollLeft = Math.max(scrollWidth - clientWidth, 1);
    const thumbLeft = (scrollLeft / maxScrollLeft) * maxThumbTravel;

    setScrollbarThumbWidth(thumbWidth);
    setScrollbarThumbLeft(thumbLeft);
  };

  React.useEffect(() => {
    updateCustomScrollbar();
    const container = horizontalScrollRef.current;
    if (!container) return;

    const handleResize = () => updateCustomScrollbar();

    container.addEventListener("scroll", updateCustomScrollbar, { passive: true });
    window.addEventListener("resize", handleResize);

    return () => {
      container.removeEventListener("scroll", updateCustomScrollbar);
      window.removeEventListener("resize", handleResize);
    };
  }, [columns, fixedColumn, rows, isLoading, loadingRows, compact]);

  const handleCustomScrollbarTrackClick = (
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    if (isThumbDragging) return;

    const container = horizontalScrollRef.current;
    if (!container) return;

    const trackRect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - trackRect.left;
    const thumbCenterX = scrollbarThumbWidth / 2;
    const targetThumbLeft = Math.min(
      Math.max(clickX - thumbCenterX, 0),
      trackRect.width - scrollbarThumbWidth,
    );
    const maxThumbTravel = Math.max(trackRect.width - scrollbarThumbWidth, 1);
    const scrollRatio = targetThumbLeft / maxThumbTravel;
    const maxScrollLeft = Math.max(container.scrollWidth - container.clientWidth, 0);
    container.scrollLeft = scrollRatio * maxScrollLeft;
  };

  const handleCustomScrollbarThumbMouseDown = (
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    const track = customScrollbarTrackRef.current;
    if (!track) return;

    event.preventDefault();
    event.stopPropagation();
    setIsThumbDragging(true);
    const trackRect = track.getBoundingClientRect();
    thumbDragOffsetX.current =
      event.clientX - (trackRect.left + scrollbarThumbLeft);
  };

  React.useEffect(() => {
    if (!isThumbDragging) return;

    const handleMouseMove = (event: MouseEvent) => {
      const container = horizontalScrollRef.current;
      const track = customScrollbarTrackRef.current;
      if (!container || !track) return;

      const trackRect = track.getBoundingClientRect();
      const maxThumbTravel = Math.max(trackRect.width - scrollbarThumbWidth, 0);
      const targetThumbLeft = Math.min(
        Math.max(event.clientX - trackRect.left - thumbDragOffsetX.current, 0),
        maxThumbTravel,
      );
      const maxScrollLeft = Math.max(container.scrollWidth - container.clientWidth, 0);
      const scrollRatio =
        maxThumbTravel > 0 ? targetThumbLeft / maxThumbTravel : 0;

      container.scrollLeft = scrollRatio * maxScrollLeft;
    };

    const handleMouseUp = () => setIsThumbDragging(false);

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isThumbDragging, scrollbarThumbWidth]);

  const allColumns = fixedColumn ? [fixedColumn, ...columns] : columns;

  // Calcular si necesitamos scroll
  const displayRows = isLoading ? loadingRows : rows.length;
  const needsScroll = maxRows !== undefined && displayRows > maxRows;

  // Altura máxima calculada con CSS calc para que respete la densidad activa.
  const maxHeight = maxRows
    ? `calc(var(--flysoft-density-datatable-row) * ${maxRows})`
    : undefined;

  // Padding de celda: lee de las variables de densidad. En modo compact, el
  // wrapping container (más abajo) ya redefine las variables al preset compact.
  // (En comfortable: px-4 py-3, equivalente al diseño previo.)
  const cellPadding =
    "px-[var(--flysoft-density-padding-x-md)] py-[var(--flysoft-density-padding-y-lg)]";
  const cellFontStyle: React.CSSProperties = {
    fontSize: "var(--flysoft-density-font-sm)",
  };

  // Verificar si alguna columna tiene footer
  const hasFooter = allColumns.some((column) => column.footer !== undefined);
  const getCellValue = (
    column: DataTableColumn<T>,
    row: T,
  ): React.ReactNode => {
    if (!column.value) return null;

    if (typeof column.value === "function") {
      return column.value(row);
    }

    // Si es string o number, puede ser un nombre de propiedad o un valor directo
    if (typeof column.value === "string" || typeof column.value === "number") {
      // Intentar obtener la propiedad del objeto si existe
      if (
        typeof column.value === "string" &&
        typeof row === "object" &&
        row !== null
      ) {
        const value = (row as Record<string, unknown>)[column.value];
        if (value !== undefined) {
          return value as string | number | React.ReactNode;
        }
      }
      // Si no es una propiedad, retornar el valor directo
      return column.value;
    }

    return column.value;
  };

  const formatValue = (
    value: string | number | React.ReactNode,
    type?: string,
  ): React.ReactNode => {
    if (React.isValidElement(value)) {
      return value;
    }

    // Convertir string a número si es necesario para currency o numeric
    let numericValue: number | null = null;
    if (typeof value === "number") {
      numericValue = value;
    } else if (
      typeof value === "string" &&
      (type === "currency" || type === "numeric")
    ) {
      const parsed = parseFloat(value);
      if (!isNaN(parsed)) {
        numericValue = parsed;
      }
    }

    if (numericValue !== null) {
      if (type === "currency") {
        // Formatear usando el locale proporcionado sin símbolo de moneda
        const parts = new Intl.NumberFormat(locale, {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).formatToParts(numericValue);

        // Construir el string sin el símbolo de moneda
        return parts
          .filter((part) => part.type !== "currency")
          .map((part) => part.value)
          .join("");
      }
      if (type === "numeric") {
        // Formatear usando el locale proporcionado
        const hasDecimals = numericValue % 1 !== 0;
        return new Intl.NumberFormat(locale, {
          minimumFractionDigits: hasDecimals ? 2 : 0,
          maximumFractionDigits: hasDecimals ? 2 : 0,
        }).format(numericValue);
      }
    }

    if (typeof value === "string" && type === "date") {
      try {
        const date = new Date(value);
        return date.toLocaleDateString(locale);
      } catch {
        return value;
      }
    }

    return value;
  };

  const getAlignmentClass = (
    align?: "left" | "right" | "center",
    type?: string,
  ): string => {
    // Las columnas de tipo 'date' siempre se alinean a la izquierda
    // Las columnas de tipo 'currency' y 'numeric' siempre se alinean a la derecha
    let effectiveAlign: "left" | "right" | "center" | undefined = align;
    if (type === "date") {
      effectiveAlign = "left";
    } else if (type === "currency" || type === "numeric") {
      effectiveAlign = "right";
    }

    switch (effectiveAlign) {
      case "right":
        return "text-right";
      case "center":
        return "text-center";
      case "left":
      default:
        return "text-left";
    }
  };

  // Convertir array de ReactNode a array de ActionItem para DropdownMenu
  const convertActionsToOptions = (
    actions: Array<React.ReactNode>,
  ): ActionItem[] => {
    return actions.map((action, index) => ({
      id: index,
      content: (
        <div
          onClick={(e) => {
            // Detener la propagación para que el onClick del DropdownMenu no interfiera
            e.stopPropagation();
          }}
        >
          {action}
        </div>
      ),
    }));
  };

  // Componente Skeleton para celdas de carga
  const SkeletonCell = () => (
    <div className="h-4 bg-[var(--color-border-default)]/40 rounded animate-pulse w-full" />
  );

  return (
    <div
      className={twMerge(
        "relative",
        className,
      )}
      data-density-override={compact ? "compact" : undefined}
      style={compact ? compactDensityOverride : undefined}
    >
      <style>{`
        .datatable-horizontal-scrollbar-hidden::-webkit-scrollbar {
          width: 0;
          height: 0;
          display: none;
        }
      `}</style>
      <div
        ref={horizontalScrollRef}
        className={twMerge(
          "overflow-x-auto datatable-horizontal-scrollbar-hidden",
          isDragging ? "select-none cursor-grabbing" : "",
        )}
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUpOrLeave}
        onMouseLeave={handleMouseUpOrLeave}
      >
        <div
          className={needsScroll ? "relative overflow-y-auto" : ""}
          style={needsScroll && maxHeight ? { maxHeight: maxHeight } : undefined}
        >
          <table className="w-full border-collapse font-[var(--font-default)]">
          <thead className={needsScroll ? "sticky top-0 z-10" : ""}>
            <tr
              className={twMerge(
                "border-b border-[var(--color-border-default)] text-[var(--color-text-primary)]",
                headerClassName,
              )}
            >
              {allColumns.map((column, index) => {
                const headerActions = column.headerActions?.();
                const hasHeaderActions =
                  headerActions && headerActions.length > 0;
                const isFixedColumn = !!fixedColumn && index === 0;

                const headerBgClasses = headerClassName
                  .split(/\s+/)
                  .filter((cls) => cls.split(":").pop()?.startsWith("bg-"))
                  .join(" ");

                return (
                  <th
                    key={index}
                    className={twMerge(
                      cellPadding,
                      "font-semibold",
                      headerBgClasses || "bg-[var(--color-bg-secondary)]",
                      getAlignmentClass(column.align, column.type),
                      isFixedColumn ? "sticky left-0 z-20" : "",
                      hasHeaderActions ? "relative" : "",
                      headerCellClassName,
                    )}
                    style={{
                      ...cellFontStyle,
                      ...(column.width ? { width: column.width } : {}),
                    }}
                  >
                    {isLoading ? (
                      <SkeletonCell />
                    ) : hasHeaderActions ? (
                      <div className="flex items-center justify-between gap-2">
                        <span>{column.header || ""}</span>
                        <DropdownMenu<ActionItem>
                          options={convertActionsToOptions(headerActions)}
                          onOptionSelected={() => {
                            // Las acciones ya manejan sus propios eventos
                          }}
                          renderOption={(item) => item.content}
                          replaceOnSingleOption={true}
                          compact={compact}
                        />
                      </div>
                    ) : (
                      column.header || ""
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? Array.from({ length: loadingRows }).map((_, rowIndex) => (
                  <tr
                    key={`skeleton-${rowIndex}`}
                    className="border-b border-[var(--color-border-default)] text-[var(--color-text-primary)]"
                  >
                    {allColumns.map((column, colIndex) => {
                      const isFixedColumn = !!fixedColumn && colIndex === 0;

                      return (
                      <td
                        key={colIndex}
                        className={twMerge(
                          cellPadding,
                          getAlignmentClass(column.align, column.type),
                          isFixedColumn ? "sticky left-0 z-10 bg-[var(--color-bg-default)]" : "",
                        )}
                        style={{
                          ...cellFontStyle,
                          ...(column.width ? { width: column.width } : {}),
                        }}
                      >
                        <SkeletonCell />
                      </td>
                      );
                    })}
                  </tr>
                ))
              : rows.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className={twMerge(
                      "group/row border-b border-[var(--color-border-default)] transition-colors hover:bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)]",
                      rowClassName?.(row),
                    )}
                  >
                    {allColumns.map((column, colIndex) => {
                      const cellValue = getCellValue(column, row);
                      const formattedValue = formatValue(
                        cellValue as string | number | React.ReactNode,
                        column.type,
                      );
                      const isFixedColumn = !!fixedColumn && colIndex === 0;
                      const tooltip = column.tooltip
                        ? column.tooltip(row)
                        : undefined;
                      const rowActions = column.actions?.(row);
                      const hasRowActions = rowActions && rowActions.length > 0;

                      return (
                        <td
                          key={colIndex}
                          className={twMerge(
                            cellPadding,
                            getAlignmentClass(column.align, column.type),
                            isFixedColumn
                              ? "sticky left-0 z-10 bg-[var(--color-bg-default)] group-hover/row:bg-[var(--color-bg-secondary)]"
                              : "",
                            typeof cellClassName === "function"
                              ? cellClassName(row, column)
                              : cellClassName,
                          )}
                          style={{
                            ...cellFontStyle,
                            ...(column.width ? { width: column.width } : {}),
                          }}
                          title={
                            tooltip
                              ? typeof tooltip === "string"
                                ? tooltip
                                : undefined
                              : undefined
                          }
                        >
                          {hasRowActions ? (
                            <div className="flex items-center justify-between gap-2">
                              <span>{formattedValue}</span>
                              <div className="lg:opacity-0 lg:group-hover/row:opacity-100 transition-opacity">
                                <DropdownMenu<ActionItem>
                                  options={convertActionsToOptions(rowActions)}
                                  onOptionSelected={() => {
                                    // Las acciones ya manejan sus propios eventos
                                  }}
                                  renderOption={(item) => item.content}
                                  replaceOnSingleOption={true}
                                  compact={compact}
                                />
                              </div>
                            </div>
                          ) : (
                            formattedValue
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
          </tbody>
          {hasFooter && (
            <tfoot className={needsScroll ? "sticky bottom-0 z-10" : ""}>
              <tr
                className={twMerge(
                  "border-t border-[var(--color-border-default)] text-[var(--color-text-primary)]",
                  footerClassName,
                )}
              >
                {allColumns.map((column, index) => {
                  const footerBgClasses = footerClassName
                    .split(/\s+/)
                    .filter((cls) => cls.split(":").pop()?.startsWith("bg-"))
                    .join(" ");
                  const isFixedColumn = !!fixedColumn && index === 0;

                  return (
                    <td
                      key={index}
                      className={twMerge(
                        cellPadding,
                        "font-semibold",
                        footerBgClasses || "bg-[var(--color-bg-secondary)]",
                        getAlignmentClass(column.align, column.type),
                        isFixedColumn ? "sticky left-0 z-20" : "",
                        footerCellClassName,
                      )}
                      style={{
                        ...cellFontStyle,
                        ...(column.width ? { width: column.width } : {}),
                      }}
                    >
                      {isLoading ? <SkeletonCell /> : column.footer || ""}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
          </table>
        </div>
      </div>
      {showCustomScrollbar && (
        <div className="mt-2 px-1">
          <div
            ref={customScrollbarTrackRef}
            className="relative h-2 rounded-full bg-[var(--color-border-default)]/40"
            onMouseDown={handleCustomScrollbarTrackClick}
          >
            <div
              className="absolute top-0 h-2 rounded-full bg-[var(--color-text-secondary)]/50 cursor-pointer"
              style={{
                width: `${scrollbarThumbWidth}px`,
                transform: `translateX(${scrollbarThumbLeft}px)`,
              }}
              onMouseDown={handleCustomScrollbarThumbMouseDown}
            />
          </div>
        </div>
      )}
    </div>
  );
};
